import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { v4 as uuidGenerator } from "uuid";
import {OrderItem} from "@/app/shop/features/orders/model/order-item.ts";
import {ShoppingCart} from "@/app/shop/model/shopping-cart.ts";
import {ShopItem} from "@/app/shop/model/shop-item.ts";

export const STORAGE_KEY = "shopping-cart";

type State = ShoppingCart;

type Action =
    | { type: "INIT"; payload: ShoppingCart }
    | { type: "ADD_SHOP_ITEM"; payload: ShopItem }
    | { type: "REMOVE_SHOP_ITEM"; payload: { orderItemUuid: string } }
    | { type: "DELETE_ORDER_ITEM"; payload: { orderItemUuid: string } }
    | { type: "SET_QUANTITY"; payload: { orderItemUuid: string; quantity: number } }
    | { type: "CLEAR_SHOPPING_CART" };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "INIT":
            return action.payload;

        case "ADD_SHOP_ITEM": {
            const existingOrderItem = state.listOrderItem.find((orderItem: OrderItem) => orderItem.shopItem.uuid === action.payload.uuid);

            const listOrderItem = existingOrderItem
                ? state.listOrderItem.map((orderItem: OrderItem) =>
                    orderItem.uuid === existingOrderItem.uuid ? { ...orderItem, quantity: orderItem.quantity + 1 } : orderItem
                )
                : [
                    ...state.listOrderItem,
                    {
                        ...new OrderItem(),
                        uuid: uuidGenerator(),
                        quantity: 1,
                        shopItem: action.payload,
                    },
                ];

            return { ...state, listOrderItem } as ShoppingCart;
        }

        case "REMOVE_SHOP_ITEM": {
            const existingOrderItem = state.listOrderItem.find((i) => i.uuid === action.payload.orderItemUuid);
            if (!existingOrderItem) return state;

            const listOrderItem =
                existingOrderItem.quantity > 1
                    ? state.listOrderItem.map((i) =>
                        i.uuid === existingOrderItem.uuid ? { ...i, quantity: i.quantity - 1 } : i
                    )
                    : state.listOrderItem.filter((i) => i.uuid !== existingOrderItem.uuid);

            return { ...state, listOrderItem } as ShoppingCart;
        }

        case "DELETE_ORDER_ITEM": {
            const idx = state.listOrderItem.findIndex((i) => i.uuid === action.payload.orderItemUuid);
            if (idx === -1) return state; // ✅ nichts zu löschen

            const listOrderItem = state.listOrderItem.filter(
                (i) => i.uuid !== action.payload.orderItemUuid
            );

            return { ...state, listOrderItem } as ShoppingCart;
        }

        case "SET_QUANTITY": {
            const quantity = Math.max(1, action.payload.quantity); // min=1 safeguard
            const listOrderItem = state.listOrderItem.map((i) =>
                i.uuid === action.payload.orderItemUuid ? { ...i, quantity: quantity } : i
            );
            return { ...state, listOrderItem } as ShoppingCart;
        }

        case "CLEAR_SHOPPING_CART":
            return new ShoppingCart();

        default:
            return state;
    }
}

type CtxValue = {
    shoppingCart: ShoppingCart;
    addShopItemToShoppingCart: (item: ShopItem) => void;
    removeShopItemFromShoppingCart: (orderItemUuid: string) => void;
    deleteOrderItemFromShoppingCart: (orderItemUuid: string) => void;
    updateQuantity: (orderItemUuid: string, quantity: number) => void;
    clearShoppingCart: () => void;

    totalItems: number; // für Badge
};

const ShoppingCartContext = createContext<CtxValue | null>(null);

export function ShoppingCartProvider({ children }: { children: React.ReactNode }) {
    const [shoppingCart, dispatch] = useReducer(reducer, new ShoppingCart());

    // init from localStorage
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            dispatch({ type: "INIT", payload: JSON.parse(stored) as ShoppingCart });
        }
    }, []);

    // persist
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(shoppingCart));
    }, [shoppingCart]);

    const value = useMemo<CtxValue>(() => {
        const totalItems = shoppingCart.listOrderItem.reduce((sum, i) => sum + i.quantity, 0);

        return {
            shoppingCart,
            addShopItemToShoppingCart: (item: ShopItem) => dispatch({ type: "ADD_SHOP_ITEM", payload: item }),
            removeShopItemFromShoppingCart: (orderItemUuid: string) => dispatch({ type: "REMOVE_SHOP_ITEM", payload: { orderItemUuid } }),
            deleteOrderItemFromShoppingCart: (orderItemUuid: string) => dispatch({ type: "DELETE_ORDER_ITEM", payload: { orderItemUuid } }),
            updateQuantity: (orderItemUuid, quantity: number) => dispatch({ type: "SET_QUANTITY", payload: { orderItemUuid, quantity } }),
            clearShoppingCart: () => dispatch({ type: "CLEAR_SHOPPING_CART" }),
            totalItems,
        };
    }, [shoppingCart]);

    return <ShoppingCartContext.Provider value={value}>{children}</ShoppingCartContext.Provider>;
}

export function useShoppingCart() {
    const ctx = useContext(ShoppingCartContext);
    if (!ctx) throw new Error("useShoppingCart must be used within ShoppingCartProvider");
    return ctx;
}
